import { Alert, Button, Checkbox, Descriptions, Divider, Input, List, Select, Space, Tag, Typography } from 'antd';
import type { LeadPolicyDocument, PolicyEditablePayload } from './types';
import type { PolicyEditableArrayKey } from './policyEditableUtils';

const { Text, Paragraph } = Typography;

type PolicyEditDraft = {
  summary: string;
  payload: PolicyEditablePayload;
};

type PolicyAnalysisDetailsProps = {
  doc: LeadPolicyDocument;
  draft?: PolicyEditDraft;
  formatCoverageEvidence: (value: unknown) => string;
  formatCoverageNotes: (value: unknown) => string;
  formatPolicyAnalysisError: (value?: string | null) => string;
  onCreateFollowUp?: (title: string, description: string) => void;
  onCreateFollowUpBatch?: (items: Array<{ title: string; description: string }>) => void;
  onUpdateDraftLine: (
    policyId: string,
    section: PolicyEditableArrayKey,
    index: number,
    field: string,
    value: unknown
  ) => void;
  onUpdateDraftSummary: (policyId: string, value: string) => void;
};

function formatPolicyPrimitive(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return normalizePolicyDisplayText(value);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatPolicyAmountNumber(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nej';
  }
  return '';
}

function formatPolicyAmountNumber(value: number): string {
  return new Intl.NumberFormat('da-DK', { maximumFractionDigits: 20 }).format(value);
}

function formatPolicyDigitSequence(value: string): string {
  const normalized = value.replace(/\./g, '');
  const numberValue = Number(normalized);
  if (!Number.isFinite(numberValue)) {
    return value;
  }
  return formatPolicyAmountNumber(numberValue);
}

function normalizePolicyDisplayText(value: string): string {
  return value
    .replace(/\bDKK\b\s*/gi, '')
    .replace(/(^|[^\d.])(\d{1,3}(?:\.\d{3})+|\d{5,})(?![\d.])/g, (_match, prefix: string, digits: string) => `${prefix}${formatPolicyDigitSequence(digits)}`)
    .replace(/([a-zæøå])(?=\d)/gi, '$1 ')
    .replace(/(\d)(?=[A-ZÆØÅa-zæøå])/g, '$1 ')
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function getPolicyRecordText(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const text = formatPolicyDisplayValue(record[key]);
    if (text) {
      return text;
    }
  }
  return '';
}

function formatPolicyDisplayValue(value: unknown): string {
  const primitive = formatPolicyPrimitive(value);
  if (primitive) {
    return primitive;
  }

  if (Array.isArray(value)) {
    return value.map(formatPolicyDisplayValue).filter(Boolean).join(' · ');
  }

  if (!value || typeof value !== 'object') {
    return '';
  }

  const record = value as Record<string, unknown>;
  const directValue = getPolicyRecordText(record, [
    'formatted_value',
    'formattedValue',
    'value',
    'amount',
    'limit',
    'sum',
    'insured_sum',
    'insuredSum',
    'deductible',
    'self_risk',
    'selvrisiko',
    'text'
  ]);

  if (directValue) {
    return directValue;
  }

  return normalizePolicyDisplayText(Object.entries(record)
    .map(([key, entryValue]) => {
      const text = formatPolicyDisplayValue(entryValue);
      return text ? text : '';
    })
    .filter(Boolean)
    .join(' · '));
}

function formatPolicyInputValue(value: unknown): string {
  return formatPolicyDisplayValue(value);
}

function formatPolicyEvidenceValue(value: unknown, fallbackFormatter?: (value: unknown) => string): string {
  const fallbackText = fallbackFormatter ? fallbackFormatter(value) : '';
  return normalizePolicyDisplayText(fallbackText || formatPolicyDisplayValue(value));
}

export default function PolicyAnalysisDetails({
  doc,
  draft,
  formatCoverageEvidence,
  formatCoverageNotes,
  formatPolicyAnalysisError,
  onCreateFollowUp,
  onCreateFollowUpBatch,
  onUpdateDraftLine,
  onUpdateDraftSummary
}: PolicyAnalysisDetailsProps) {
  if (doc.analysis_status === 'FAILED') {
    return (
      <Alert
        type="error"
        message="AI analysen fejlede"
        description={formatPolicyAnalysisError(doc.analysis_error)}
        showIcon
      />
    );
  }

  const isEditing = Boolean(draft);
  const payload =
    (isEditing
      ? draft?.payload
      : doc.analysis_edit
        ? (doc.effective_analysis_payload || doc.analysis_payload)
        : doc.analysis_payload) || null;
  if (!payload) {
    return <Text type="secondary">Ingen analyse tilgængelig endnu.</Text>;
  }

  const summaryValue = isEditing
    ? draft?.summary ?? ''
    : doc.analysis_edit?.summary ?? doc.effective_analysis_summary ?? doc.analysis_summary ?? '';
  const coverages = (Array.isArray((payload as any).coverages) ? (payload as any).coverages : []) as Array<Record<string, any>>;
  const sums = (Array.isArray((payload as any).sums) ? (payload as any).sums : []) as Array<Record<string, any>>;
  const excelFields = (Array.isArray((payload as any).excel_fields) ? (payload as any).excel_fields : []) as Array<Record<string, any>>;
  const attention = (Array.isArray((payload as any).attention_points) ? (payload as any).attention_points : []) as Array<Record<string, any>>;
  const uncertain = (Array.isArray((payload as any).uncertain) ? (payload as any).uncertain : []) as Array<Record<string, any>>;
  const meta = (payload as any).meta || null;

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Text strong>Forsikringsanalyse</Text>

      {isEditing ? (
        <Input.TextArea
          value={summaryValue}
          rows={3}
          placeholder="Opsummering"
          onChange={(event) => onUpdateDraftSummary(doc.id, event.target.value)}
        />
      ) : summaryValue ? (
        <Paragraph>{summaryValue}</Paragraph>
      ) : null}

      {meta ? (
        <Descriptions size="small" column={1}>
          {meta.document_title ? (
            <Descriptions.Item label="Dokument">
              {meta.document_title}
            </Descriptions.Item>
          ) : null}
          {meta.insurer ? <Descriptions.Item label="Selskab">{meta.insurer}</Descriptions.Item> : null}
          {meta.policy_number ? (
            <Descriptions.Item label="Policenr.">{meta.policy_number}</Descriptions.Item>
          ) : null}
          {meta.effective_period?.from || meta.effective_period?.to ? (
            <Descriptions.Item label="Periode">
              {[meta.effective_period?.from, meta.effective_period?.to].filter(Boolean).join(' → ') || '—'}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      ) : null}

      <Divider style={{ margin: '4px 0' }} />

      <Text strong>Dækninger</Text>
      {coverages.length === 0 ? (
        <Text type="secondary">Ingen dækninger fundet.</Text>
      ) : (
        <List
          size="small"
          dataSource={coverages}
          renderItem={(item, index) => (
            <List.Item key={`${item?.line_id ?? item?.name ?? 'coverage'}-${index}`}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                {isEditing ? (
                  <Space size={8} wrap>
                    <Checkbox
                      checked={!Boolean(item?.disabled)}
                      onChange={(event) =>
                        onUpdateDraftLine(doc.id, 'coverages', index, 'disabled', !event.target.checked)
                      }
                    >
                      Aktiv linje
                    </Checkbox>
                  </Space>
                ) : null}
                {isEditing ? (
                  <Input
                    style={{ width: '100%' }}
                    value={formatPolicyInputValue(item?.name)}
                    placeholder="Dækning"
                    onChange={(event) => onUpdateDraftLine(doc.id, 'coverages', index, 'name', event.target.value)}
                  />
                ) : (
                  <Text strong delete={Boolean(item?.disabled)}>{formatPolicyDisplayValue(item?.name) || 'Ukendt dækning'}</Text>
                )}
                {isEditing ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 8,
                      width: '100%'
                    }}
                  >
                    <Input
                      style={{ width: '100%' }}
                      value={formatPolicyInputValue(item?.limit)}
                      placeholder="Sum"
                      onChange={(event) => onUpdateDraftLine(doc.id, 'coverages', index, 'limit', event.target.value)}
                    />
                    <Input
                      style={{ width: '100%' }}
                      value={formatPolicyInputValue(item?.deductible)}
                      placeholder="Selvrisiko"
                      onChange={(event) =>
                        onUpdateDraftLine(doc.id, 'coverages', index, 'deductible', event.target.value)
                      }
                    />
                    <Input
                      style={{ width: '100%' }}
                      value={formatPolicyInputValue(item?.forsikringssted)}
                      placeholder="Forsikringssted"
                      onChange={(event) =>
                        onUpdateDraftLine(doc.id, 'coverages', index, 'forsikringssted', event.target.value)
                      }
                    />
                  </div>
                ) : (
                  <Text type="secondary" delete={Boolean(item?.disabled)}>
                    {formatPolicyDisplayValue(item?.limit) ? `Sum: ${formatPolicyDisplayValue(item.limit)}` : 'Sum: —'}
                    {formatPolicyDisplayValue(item?.deductible) ? ` · Selvrisiko: ${formatPolicyDisplayValue(item.deductible)}` : ''}
                  </Text>
                )}
                {!isEditing && item?.forsikringssted ? (
                  <Text delete={Boolean(item?.disabled)}>{`Forsikringssted: ${formatPolicyDisplayValue(item.forsikringssted)}`}</Text>
                ) : null}
                {isEditing ? (
                  <Input.TextArea
                    style={{ width: '100%' }}
                    value={formatPolicyInputValue(item?.notes)}
                    rows={2}
                    placeholder="Noter"
                    onChange={(event) => onUpdateDraftLine(doc.id, 'coverages', index, 'notes', event.target.value)}
                  />
                ) : item?.notes ? (
                  <Text delete={Boolean(item?.disabled)}>{formatCoverageNotes(item.notes)}</Text>
                ) : null}
                {item?.evidence ? <Text type="secondary">Kilde: {formatPolicyEvidenceValue(item.evidence, formatCoverageEvidence)}</Text> : null}
              </Space>
            </List.Item>
          )}
        />
      )}

      <Text strong>Summer</Text>
      {sums.length === 0 ? (
        <Text type="secondary">Ingen summer fundet.</Text>
      ) : (
        <List
          size="small"
          dataSource={sums}
          renderItem={(item, index) => (
            <List.Item key={`${item?.line_id ?? item?.label ?? 'sum'}-${index}`}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                {isEditing ? (
                  <Checkbox
                    checked={!Boolean(item?.disabled)}
                    onChange={(event) =>
                      onUpdateDraftLine(doc.id, 'sums', index, 'disabled', !event.target.checked)
                    }
                  >
                    Aktiv linje
                  </Checkbox>
                ) : null}
                {isEditing ? (
                  <Input
                    style={{ width: '100%' }}
                    value={formatPolicyInputValue(item?.label)}
                    placeholder="Etiket"
                    onChange={(event) => onUpdateDraftLine(doc.id, 'sums', index, 'label', event.target.value)}
                  />
                ) : (
                  <Text strong delete={Boolean(item?.disabled)}>{formatPolicyDisplayValue(item?.label) || 'Sum'}</Text>
                )}
                {isEditing ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 8,
                      width: '100%'
                    }}
                  >
                    <Input
                      style={{ width: '100%' }}
                      value={formatPolicyInputValue(item?.amount)}
                      placeholder="Beløb"
                      onChange={(event) => onUpdateDraftLine(doc.id, 'sums', index, 'amount', event.target.value)}
                    />
                  </div>
                ) : (
                  <Text delete={Boolean(item?.disabled)}>
                    {formatPolicyDisplayValue(item?.amount) || '—'}
                  </Text>
                )}
                {item?.evidence ? <Text type="secondary">“{formatPolicyEvidenceValue(item.evidence)}”</Text> : null}
              </Space>
            </List.Item>
          )}
        />
      )}

      <Text strong>Excel-felter</Text>
      {excelFields.length === 0 ? (
        <Text type="secondary">Ingen Excel-felter fundet.</Text>
      ) : (
        <List
          size="small"
          dataSource={excelFields}
          renderItem={(item, index) => (
            <List.Item key={`${item?.line_id ?? item?.field_key ?? item?.label ?? 'excel'}-${index}`}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Text strong>{formatPolicyDisplayValue(item?.label) || formatPolicyDisplayValue(item?.field_key) || 'Excel-felt'}</Text>
                <Text type="secondary">
                  {formatPolicyDisplayValue(item?.value) || '—'}
                  {formatPolicyDisplayValue(item?.currency) ? ` ${formatPolicyDisplayValue(item?.currency)}` : ''}
                </Text>
                {item?.evidence ? <Text type="secondary">“{formatPolicyEvidenceValue(item.evidence)}”</Text> : null}
              </Space>
            </List.Item>
          )}
        />
      )}

      <Text strong>Opmærksomhedspunkter</Text>
      {attention.length === 0 ? (
        <Text type="secondary">Ingen opmærksomhedspunkter fundet.</Text>
      ) : (
        <List
          size="small"
          dataSource={attention}
          renderItem={(item, index) => (
            <List.Item key={`${item?.line_id ?? item?.label ?? 'attention'}-${index}`}>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                {isEditing ? (
                  <Checkbox
                    checked={!Boolean(item?.disabled)}
                    onChange={(event) =>
                      onUpdateDraftLine(doc.id, 'attention_points', index, 'disabled', !event.target.checked)
                    }
                  >
                    Aktiv linje
                  </Checkbox>
                ) : null}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isEditing ? 'minmax(280px, 1fr) minmax(140px, 200px)' : '1fr',
                    gap: 8,
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  {isEditing ? (
                    <Input
                      style={{ width: '100%' }}
                      value={formatPolicyInputValue(item?.label)}
                      placeholder="Etiket"
                      onChange={(event) =>
                        onUpdateDraftLine(doc.id, 'attention_points', index, 'label', event.target.value)
                      }
                    />
                  ) : (
                    <Text strong delete={Boolean(item?.disabled)}>{formatPolicyDisplayValue(item?.label) || 'Opmærksomhedspunkt'}</Text>
                  )}
                  {isEditing ? (
                    <Select
                      value={item?.severity || undefined}
                      placeholder="Severity"
                      style={{ width: '100%' }}
                      allowClear
                      options={[
                        { value: 'low', label: 'low' },
                        { value: 'medium', label: 'medium' },
                        { value: 'high', label: 'high' }
                      ]}
                      onChange={(value) =>
                        onUpdateDraftLine(doc.id, 'attention_points', index, 'severity', value || null)
                      }
                    />
                  ) : item?.severity ? (
                    <Tag color={item.severity === 'high' ? 'red' : item.severity === 'medium' ? 'gold' : 'blue'}>
                      {item.severity}
                    </Tag>
                  ) : null}
                </div>
                {isEditing ? (
                  <Input.TextArea
                    style={{ width: '100%' }}
                    value={formatPolicyInputValue(item?.details)}
                    rows={2}
                    placeholder="Detaljer"
                    onChange={(event) =>
                      onUpdateDraftLine(doc.id, 'attention_points', index, 'details', event.target.value)
                    }
                  />
                ) : item?.details ? (
                  <Text delete={Boolean(item?.disabled)}>{formatPolicyDisplayValue(item.details)}</Text>
                ) : null}
                {item?.evidence ? <Text type="secondary">“{formatPolicyEvidenceValue(item.evidence)}”</Text> : null}
              </Space>
            </List.Item>
          )}
        />
      )}

      {uncertain.length > 0 ? (
        <>
          <Space size={8} align="center" wrap>
            <Text strong>Usikkerheder</Text>
            {!isEditing && onCreateFollowUpBatch ? (
              <Button
                size="small"
                onClick={() =>
                  onCreateFollowUpBatch(
                    uncertain.map((item) => ({
                      title: `Afklaring: ${formatPolicyDisplayValue(item?.question) || 'ukendt spørgsmål'}`,
                      description: [
                        `Dokument: ${doc.file_name}`,
                        `Spørgsmål: ${formatPolicyDisplayValue(item?.question) || '—'}`,
                        `Begrundelse: ${formatPolicyDisplayValue(item?.reason) || '—'}`,
                        item?.evidence ? `Kilde: ${formatPolicyEvidenceValue(item.evidence)}` : null
                      ].filter(Boolean).join('\n')
                    }))
                  )
                }
              >
                Opret opgaver for alle
              </Button>
            ) : null}
          </Space>
          <List
            size="small"
            dataSource={uncertain}
            renderItem={(item, index) => (
              <List.Item key={`${item?.line_id ?? item?.question ?? 'uncertain'}-${index}`}>
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  {isEditing ? (
                    <Checkbox
                      checked={!Boolean(item?.disabled)}
                      onChange={(event) =>
                        onUpdateDraftLine(doc.id, 'uncertain', index, 'disabled', !event.target.checked)
                      }
                    >
                      Aktiv linje
                    </Checkbox>
                  ) : null}
                  {isEditing ? (
                    <Input
                      style={{ width: '100%' }}
                      value={formatPolicyInputValue(item?.question)}
                      placeholder="Spørgsmål"
                      onChange={(event) =>
                        onUpdateDraftLine(doc.id, 'uncertain', index, 'question', event.target.value)
                      }
                    />
                  ) : (
                    <Text strong delete={Boolean(item?.disabled)}>{formatPolicyDisplayValue(item?.question) || 'Afklaring'}</Text>
                  )}
                  {!isEditing && onCreateFollowUp ? (
                    <Button
                      size="small"
                      onClick={() =>
                        onCreateFollowUp(
                          `Afklaring: ${formatPolicyDisplayValue(item?.question) || 'ukendt spørgsmål'}`,
                          [
                            `Dokument: ${doc.file_name}`,
                            `Spørgsmål: ${formatPolicyDisplayValue(item?.question) || '—'}`,
                            `Begrundelse: ${formatPolicyDisplayValue(item?.reason) || '—'}`,
                            item?.evidence ? `Kilde: ${formatPolicyEvidenceValue(item.evidence)}` : null
                          ].filter(Boolean).join('\n')
                        )
                      }
                    >
                      Opret opgave
                    </Button>
                  ) : null}
                  {isEditing ? (
                    <Input.TextArea
                      style={{ width: '100%' }}
                      value={formatPolicyInputValue(item?.reason)}
                      rows={2}
                      placeholder="Begrundelse"
                      onChange={(event) =>
                        onUpdateDraftLine(doc.id, 'uncertain', index, 'reason', event.target.value)
                      }
                    />
                  ) : item?.reason ? (
                    <Text delete={Boolean(item?.disabled)}>{formatPolicyDisplayValue(item.reason)}</Text>
                  ) : null}
                  {item?.evidence ? <Text type="secondary">“{formatPolicyEvidenceValue(item.evidence)}”</Text> : null}
                </Space>
              </List.Item>
            )}
          />
        </>
      ) : null}
    </Space>
  );
}
